import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import { Divider, TextField, Typography, FormControlLabel, Switch } from '@mui/material';
import Stack from '@mui/material/Stack';
import { useCreatePrescription } from '../../../hooks/prescriptions/useCreatePrescription';
import { useUpdatePrescription } from '../../../hooks/prescriptions/useUpdatePrescription';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { isPastDateNoTime } from '../../../utils/isPastDateNoTime';
import dayjs from 'dayjs';
import DatePickerMui from '../../common/controls/DatePickerMui';

export default function PrescriptionForm({ successCallback = null, history = null, historyEntry = null, pet = null, prescription = null, mode = "create", open, onClose }) {

    const { t } = useTranslation('common'); 

    const [date, setDate] = React.useState("");
    const [validityDate, setValidityDate] = React.useState("");
    const [content, setContent] = React.useState("");
    const [disabled, setDisabled] = React.useState(false);

    const { create, creating } = useCreatePrescription(successCallback);
    const { update, updating } = useUpdatePrescription(successCallback);

    const { showSnackbar } = useSnackbar();

    React.useEffect(() => {
        // reset field when there are changes (e.g. when opening edit)
        if (open)
        {
          setDate(prescription?.date || dayjs().format("YYYY-MM-DD"));
          setValidityDate(prescription?.validity_date || "");
          setContent(prescription?.content || "");
          setDisabled(prescription ? !!prescription.disabled : false);
        }
    }, [prescription, open]);

    const handleDateChange = (date) => {
      setDate(date);
    };

    const handleValidityDateChange = (date) => {
      setValidityDate(date);
    };

    const handleSave = async () => {
        if (!content.trim()) {
          showSnackbar(t('prescriptions.messages.content_required'), "warning");
          return;
        }
        if (!date.trim()) {
          showSnackbar(t('prescriptions.messages.date_required'), "warning");
          return;
        }
        if ((mode === "create") && isPastDateNoTime(date)) {
          showSnackbar(t('prescriptions.messages.date_must_be_future'), "warning");
          return;
        }

        const dateObj = new Date(date);
        const validityDateObj = new Date(validityDate);
        if (validityDateObj <= dateObj) {
          showSnackbar(t('prescriptions.messages.validity_date_must_be_later'), "warning");
          return;
        }

        try 
        {
          if (mode === "create") {
            try {
              await create({ 
                pet_id: pet.id,
                pet_owner_id: pet.owner.id,
                history_id: history ? history.id : null,
                history_entry_id: historyEntry ? historyEntry.id : null,
                date: date,
                validity_date: validityDate || null,
                content: content,
                disabled: disabled
              });
              showSnackbar(t('prescriptions.messages.create_success'), "success");
              // clear field after save
              setDate("");
              setValidityDate("");
              setContent("");
              setDisabled(false);
              onClose();     // close dialog
            } catch(error)
            {
              showSnackbar(t('prescriptions.messages.save_fail'), "error");
            }  
          } else if (mode === "edit" && prescription) {
              try {
                await update(prescription.id, { 
                  date: date,
                  validity_date: validityDate || null,
                  content: content,
                  disabled: disabled
                });
                showSnackbar(t('prescriptions.messages.update_success'), "success");
                // clear field after save
                setDate("");
                setValidityDate("");
                setContent("");
                setDisabled(false);
                onClose();     // close dialog
              } catch(error)
              {
                showSnackbar(t('prescriptions.messages.save_fail'), "error");
              }
          }
        } catch (err) {
          showSnackbar(t('prescriptions.messages.save_fail'), "error");
        }
    };

    const handleCancel = () => {
      setDate("");
      setValidityDate("");
      setContent("");
      setDisabled(false);
      onClose(); 
    };

    return (
        <>
        <Dialog onClose={handleCancel} open={open} disableRestoreFocus>

        <DialogTitle>{mode === "create" ? "Create Prescription" : "Edit Prescription"}</DialogTitle>

        <DialogContent dividers>
            <Typography variant="body2" sx={{ mb: 0, color: 'gray' }}>Patient</Typography>
            <Typography variant="body1" sx={{ mt: 0 }}>{`${pet?.name} (${pet?.owner?.last_name}, ${pet?.owner?.first_name})`}</Typography>
            
            <Divider sx={{ my: 2 }}/>
            <Stack spacing={2}>

              <DatePickerMui
                value={date ? dayjs(date) : dayjs()}
                label="Date"
                onChange={handleDateChange}
              />
          
              <DatePickerMui
                value={validityDate ? dayjs(validityDate) : null}
                label="Valid Until (Optional)"
                onChange={handleValidityDateChange}
              />
              
              <TextField
                autoFocus
                label="Medications and Instructions"
                variant="outlined"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                maxRows={7}
              />

              {mode === "edit" && (
                <FormControlLabel
                control={
                    <Switch
                    checked={disabled}
                    onChange={(e) => setDisabled(e.target.checked)}
                    color="primary"
                    />
                }
                label="Disabled"
                />
                )}
              
            </Stack>
        </DialogContent>

        <DialogActions>
            <Button disabled={(creating || updating) } onClick={handleSave}>
              Save
            </Button>
            <Button onClick={handleCancel}>Cancel</Button>
        </DialogActions>
        </Dialog>
        </>
    );
}